import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar, Badge, Box, Button, Divider, Drawer, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack,
  Toolbar, Tooltip,
} from "@mui/material";
import {
  AccountCircleOutlined, AutoAwesomeOutlined, CloseRounded, HomeOutlined,
  HelpOutlineRounded, Inventory2Outlined, LocalFireDepartmentOutlined, MenuRounded,
  ReceiptLongOutlined, ShoppingBagOutlined,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { usePage } from "../context/PageContext";
import HelpVideoDialog from "./HelpVideoDialog";

const MainNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { items } = useCart();
  const { activeSection, changeSection } = usePage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const isLogged = Boolean(token && user);
  const inHome = location.pathname === "/home";
  const inLogin = location.pathname === "/login";
  const inDashboard = location.pathname.startsWith("/dashboard");
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const homeLinks = useMemo(() => [
    { id: "get-started-section", label: "How it works" },
    { id: "about-section", label: "Our story" },
    { id: "contact-section", label: "Contact" },
  ], []);
  const shopLinks = [
    { key: "Products", label: "Shop", icon: <Inventory2Outlined /> },
    { key: "Seasonal", label: "Seasonal", icon: <AutoAwesomeOutlined /> },
    { key: "Top Sellers", label: "Best sellers", icon: <LocalFireDepartmentOutlined /> },
    { key: "Orders", label: "Orders", icon: <ReceiptLongOutlined /> },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const openHelp = () => setHelpOpen(true);
    window.addEventListener("rustic:open-help", openHelp);
    return () => window.removeEventListener("rustic:open-help", openHelp);
  }, []);

  const scrollTo = (id) => {
    const run = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (!inHome) navigate(`/home#${id}`); else run();
    setDrawerOpen(false);
  };
  const openSection = (key) => {
    changeSection(key);
    if (!inDashboard) navigate("/dashboard");
    setDrawerOpen(false);
  };
  const handleLogout = () => { setAccountAnchor(null); logout(); navigate("/home", { replace: true }); };

  const heroTop = inHome && !scrolled;
  const navColor = heroTop ? "#fffaf2" : "text.primary";

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <AppBar
        elevation={0}
        color="transparent"
        sx={{
          position: "fixed", color: navColor,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          bgcolor: heroTop ? "rgba(8,24,16,.86)" : "rgba(255,253,248,.94)",
          borderBottom: heroTop ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(24,36,29,.12)",
          backdropFilter: "blur(18px)",
          boxShadow: heroTop ? "0 18px 60px rgba(0,0,0,.18)" : "0 14px 50px rgba(24,36,29,.08)",
          transition: "background-color 220ms ease, color 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 72, md: 84 }, maxWidth: 1480, width: "100%", mx: "auto", px: { xs: 1.5, sm: 2.5, md: 5 } }}>
          <IconButton onClick={() => setDrawerOpen(true)} sx={{ display: { md: "none" }, color: "inherit", mr: 1 }} aria-label="Open navigation"><MenuRounded /></IconButton>
          <Box onClick={() => navigate("/home")} sx={{ display: "flex", alignItems: "center", cursor: "pointer", mr: "auto" }}>
            <Box component="img" src="/Logo-Rustic.svg" alt="Rustic World" sx={{ height: { xs: 38, sm: 44, md: 56 }, width: "auto", filter: heroTop ? "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,.2))" : "none" }} />
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
            {isLogged ? shopLinks.map((item) => (
              <Button key={item.key} onClick={() => openSection(item.key)} color="inherit" sx={{ px: 1.6, bgcolor: inDashboard && activeSection === item.key ? "rgba(184,103,62,.11)" : "transparent" }}>{item.label}</Button>
            )) : homeLinks.map((item) => (
              <Button key={item.id} onClick={() => scrollTo(item.id)} color="inherit" sx={{ px: 1.8 }}>{item.label}</Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: { xs: 0, md: 2 } }}>
            <Button
              color="inherit"
              onClick={() => setHelpOpen(true)}
              startIcon={<HelpOutlineRounded />}
              sx={{ display: { xs: "none", lg: "inline-flex" }, minHeight: 48, px: 1.75 }}
            >
              Help
            </Button>
            <Tooltip title="How to use this website">
              <IconButton
                color="inherit"
                onClick={() => setHelpOpen(true)}
                aria-label="How to use this website"
                sx={{ display: { xs: "inline-flex", lg: "none" } }}
              >
                <HelpOutlineRounded />
              </IconButton>
            </Tooltip>
            {isLogged ? (
              <>
                <Tooltip title="Shopping bag">
                  <IconButton color="inherit" onClick={() => openSection("Cart")} aria-label={`Shopping bag with ${totalItems} items`}>
                    <Badge badgeContent={totalItems} color="secondary"><ShoppingBagOutlined /></Badge>
                  </IconButton>
                </Tooltip>
                <Button color="inherit" onClick={(event) => setAccountAnchor(event.currentTarget)} startIcon={<AccountCircleOutlined />} sx={{ display: { xs: "none", sm: "inline-flex" }, ml: 0.5 }}>{user?.name || "Account"}</Button>
                <IconButton color="inherit" onClick={(event) => setAccountAnchor(event.currentTarget)} sx={{ display: { sm: "none" } }} aria-label="Account"><AccountCircleOutlined /></IconButton>
              </>
            ) : !inLogin ? (
              <Button variant="contained" color="secondary" onClick={() => navigate("/login")} sx={{ ml: { xs: .5, sm: 1 }, minWidth: 104, minHeight: 50, px: { xs: 1.5, sm: 2.75 }, fontSize: 16 }}>
                Sign in
              </Button>
            ) : null}
          </Stack>
        </Toolbar>
      </AppBar>

      <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem onClick={() => openSection("Orders")}>My orders</MenuItem>
        <MenuItem onClick={handleLogout}>Log out</MenuItem>
      </Menu>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 304, p: 2, bgcolor: "background.default" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box component="img" src="/Logo-Rustic.svg" alt="Rustic World" sx={{ height: 48 }} />
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><CloseRounded /></IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <List sx={{ display: "grid", gap: 0.5 }}>
          <ListItemButton onClick={() => { navigate("/home"); setDrawerOpen(false); }} sx={{ borderRadius: 2 }}><ListItemIcon><HomeOutlined /></ListItemIcon><ListItemText primary="Home" /></ListItemButton>
          {isLogged ? shopLinks.map((item) => (
            <ListItemButton key={item.key} selected={inDashboard && activeSection === item.key} onClick={() => openSection(item.key)} sx={{ borderRadius: 2 }}><ListItemIcon>{item.icon}</ListItemIcon><ListItemText primary={item.label} /></ListItemButton>
          )) : homeLinks.map((item) => (
            <ListItemButton key={item.id} onClick={() => scrollTo(item.id)} sx={{ borderRadius: 2 }}><ListItemText primary={item.label} /></ListItemButton>
          ))}
          {isLogged && <ListItemButton onClick={() => openSection("Cart")} sx={{ borderRadius: 2 }}><ListItemIcon><ShoppingBagOutlined /></ListItemIcon><ListItemText primary={`Shopping bag (${totalItems})`} /></ListItemButton>}
          <ListItemButton onClick={() => { setHelpOpen(true); setDrawerOpen(false); }} sx={{ borderRadius: 2 }}>
            <ListItemIcon><HelpOutlineRounded /></ListItemIcon>
            <ListItemText primary="How to use this website" secondary="Watch the step-by-step video" />
          </ListItemButton>
        </List>
      </Drawer>
      <HelpVideoDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default MainNav;
